import axios, { type AxiosRequestConfig } from 'axios';
import { stringify } from 'querystring';
import {
  isRestrictedRegionError,
  notifyRestrictedRegionDetected,
} from '@/lib/compliance/restricted-region';

type ApiOption = AxiosRequestConfig;

function getBaseApiUrl(): string {
  const raw = process.env.DEN_API_URL;
  if (!raw) throw new Error('DEN_API_URL is not set');
  return raw.endsWith('/') ? raw.slice(0, -1) : raw;
}

function createUrl(path: string): string {
  const base = getBaseApiUrl();
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${base}/${path}`;
}

const defaultHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
};

function withDefaultHeaders(option: ApiOption = {}): AxiosRequestConfig {
  return {
    ...option,
    withCredentials: true,
    headers: { ...defaultHeaders, ...(option.headers ?? {}) },
  };
}

/**
 * Unwraps Axios `{ data }` responses while translating restricted-region errors
 * into the global blocked-region event before rethrowing.
 */
async function unwrapResponse<R>(request: Promise<{ data: R }>): Promise<R> {
  try {
    const response = await request;
    return response.data;
  } catch (error) {
    // Case: restricted-region error — notify user via notifyRestrictedRegionDetected() then rethrow the original error.
    if (isRestrictedRegionError(error)) {
      notifyRestrictedRegionDetected();
    }

    throw error;
  }
}

// Intentionally not using a shared instance to follow caller's preference.

const denApi = {
  async get<R, I = unknown>(
    path: string,
    data?: I,
    option?: ApiOption
  ): Promise<R> {
    let url = createUrl(path);
    if (data) {
      url += `?${stringify(data)}`;
    }
    // withCredentials adds cookies; it does not transmit client IP
    return await unwrapResponse(
      axios.get<R>(url, {
        ...withDefaultHeaders(option),
        // query already stringified into url
      })
    );
  },
  async post<R, I = unknown>(
    path: string,
    data: I,
    option?: ApiOption
  ): Promise<R> {
    const url = createUrl(path);
    return await unwrapResponse(
      axios.post<R>(url, data, {
        ...withDefaultHeaders(option),
      })
    );
  },
  async put<R, I = unknown>(
    path: string,
    data: I,
    option?: ApiOption
  ): Promise<R> {
    const url = createUrl(path);
    return await unwrapResponse(
      axios.put<R>(url, data, {
        ...withDefaultHeaders(option),
      })
    );
  },
  async patch<R, I = unknown>(
    path: string,
    data: I,
    option?: ApiOption
  ): Promise<R> {
    const url = createUrl(path);
    return await unwrapResponse(
      axios.patch<R>(url, data, {
        ...withDefaultHeaders(option),
      })
    );
  },
  async delete<R>(path: string, option?: ApiOption): Promise<R> {
    const url = createUrl(path);
    return await unwrapResponse(
      axios.delete<R>(url, {
        ...withDefaultHeaders(option),
      })
    );
  },
};

export default denApi;
